import React, { FC, ChangeEvent, useState, useEffect } from 'react';

// context
import {
  useConfigSelectors,
  useInfringementRulesSelectors,
  useLanguageSelectors,
} from 'src/app/reducers';
import { useInfringementDataTable } from 'src/@infringements/reducers';
// common components
import { Filter, IDFilter } from 'src/componentsMigrated';
import {
  FilterRow,
  RangeSelector,
  validateTimeFormat,
  isValidTime,
  onCloseTimeDialogValidation,
  isCompletedTimeFormat,
  IDropdownItem,
} from '@ems/client-design-system';
// store
import { filterStore } from 'src/@infringements/stores/filterStore';
// function
import { translateLabels } from 'src/@infringements/functions';
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'src/utils';
import { useSessionStorage } from 'src/utils/hooks';
// interfaces
import { ITableFilterItem } from 'src/@infringements/interfaces';
// constants
import { NULL_VALUE } from 'src/constants';
import { DYNAMIC_TILE_SERVER } from 'src/app/featureToggles';
import { InfringementTableDataActionType } from '../reducers/tableData';
import { InfringementSummaryFilter } from 'src/app/gql/__generated__/hooks';

export const FilterContainer: FC<any> = ({
  source = '',
  availableCategoryList,
}: {
  source: string;
  availableCategoryList: string[];
}) => {
  const {
    state: {
      variables: { filter: infringementFilterState },
    },
    dispatch: infringementFilterDispatch,
  } = useInfringementDataTable();

  // Session storage for acid values
  const [, setStoredAcid] = useSessionStorage('', 'acidSearchValue');

  // Temp filter store
  const [tempAcid, setTempAcid] = useState<string>(infringementFilterState.acids?.join(', ') || '');
  // Technically InputTimePeriod type, but using a simple object for error prevention
  const [tempTimeRange, setTempTimeRange] = useState<{ start: string; end: string }>(
    infringementFilterState.timePeriod
      ? {
          start: convertSecondsToTimeString(infringementFilterState.timePeriod?.start || 0),
          end: convertSecondsToTimeString(infringementFilterState.timePeriod?.end || 0),
        }
      : undefined
  );
  const [timeLabel, setTimeLabel] = useState<string>();

  // Handlers
  const onAcidChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // Check total length including commas (max 35 chars + commas)
    if (value.length > 40) {
      return;
    }
    const upperValue = value.toUpperCase();
    setTempAcid(upperValue);
    setStoredAcid(upperValue);
  };

  const handleAcidSubmit = (clear = false) => {
    const acids = clear ? null : tempAcid.split(',').map(acid => acid.trim().toUpperCase());
    infringementFilterDispatch({
      type: InfringementTableDataActionType.UPDATE_FILTER,
      payload: { filter: 'acids', value: acids },
    });
  };

  const handleSetTimeRange = (timeRange: { start: string; end: string }) => {
    setTempTimeRange(timeRange);
  };

  // Configuration
  const configSelectors = useConfigSelectors();
  const FEATURE_FLAG_DYNAMIC_TILE_SERVER = configSelectors.isFeatureAvailable(DYNAMIC_TILE_SERVER);

  const {
    infringements: {
      filter: { nullable, showSelectedIcons },
    },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      labels: {
        acid,
        filters: { clear: clearValue, filter: filterValue, clearFilters },
      },
      hints: { loading, noMatchesFound },
      filters: { time: languageData },
      lists: {
        infringementFilterCategories,
        aircraftCategories,
        ruleTypes,
        typeCategories,
        severities,
        extraFilterValues,
        infringementStatuses,
        vectored,
      },
    },
  } = languageSelectors.getLanguage();

  const dataListUpdated = capitalizeObjectKeys({
    ...aircraftCategories,
    ...extraFilterValues,
    ...ruleTypes,
    ...typeCategories,
    ...severities,
    ...infringementStatuses,
    ...vectored,
  });

  const filterItems = filterStore.getAllFilterOptions();
  const infringementRulesSelector = useInfringementRulesSelectors();
  const infringementRulesList: IDropdownItem[] = infringementRulesSelector
    .getRules()
    .map(({ name }) => ({ key: name, label: name }));

  const clearAllFilters = () => {
    infringementFilterDispatch({ type: InfringementTableDataActionType.CLEAR_FILTERS });
    setTempAcid('');
    setStoredAcid('');
    setTempTimeRange({ start: '', end: '' });
  };

  const NullValue = { key: NULL_VALUE, label: NULL_VALUE, icon: undefined };
  const filterComponents: JSX.Element[] = [];

  availableCategoryList.map((category: string) => {
    if (category === 'acid' && FEATURE_FLAG_DYNAMIC_TILE_SERVER) {
      filterComponents.push(
        <IDFilter
          categoryName={category}
          key="filter-id-selector"
          value={tempAcid}
          onChange={onAcidChange}
          onSubmit={handleAcidSubmit}
          selectedID={tempAcid}
          onClearSelect={() => {
            setTempAcid('');
            setStoredAcid('');
            handleAcidSubmit(true);
          }}
          languageData={{
            title: acid,
            noMatchesFound,
            loading,
          }}
        />
      );
    } else if (category === 'ruleNames') {
      // If the category is 'ruleNames
      const transformedItems =
        nullable && nullable.includes(category)
          ? [NullValue, ...infringementRulesList]
          : infringementRulesList;
      filterComponents.push(
        <Filter
          key={category}
          categoryName={infringementFilterCategories[category]}
          filterItems={translateLabels(transformedItems, dataListUpdated)}
          selectedItems={(infringementFilterState.ruleNames || []).map(item => ({
            key: item,
            label: item,
          }))}
          updateItems={(items: ITableFilterItem[]) => {
            infringementFilterDispatch({
              type: InfringementTableDataActionType.UPDATE_FILTER,
              payload: { filter: 'ruleNames', value: items.map(item => item.key) },
            });
          }}
          iconCategories={showSelectedIcons.map(
            (category: string) => infringementFilterCategories[category]
          )}
          languageData={{ clearValue, filterValue, noMatchesFound }}
        />
      );
    } else if (category === 'vectored') {
      const originalItems = filterItems[category];
      if (originalItems !== undefined && originalItems.length > 0) {
        const transformedItems =
          nullable && nullable.includes(category) ? [NullValue, ...originalItems] : originalItems;
        const selectedItems = (() => {
          if (infringementFilterState.includeOperationTags?.some(tag => tag.name === 'Vector')) {
            return [{ key: 'Vectored', label: 'Vectored' }];
          } else if (
            infringementFilterState.excludeOperationTags?.some(tag => tag.name === 'Vector')
          ) {
            return [{ key: NULL_VALUE, label: NULL_VALUE }];
          }
          return [];
        })();

        filterComponents.push(
          <Filter
            type="single"
            showSelectAll={false}
            key={category}
            categoryName={infringementFilterCategories[category]}
            filterItems={translateLabels(transformedItems, dataListUpdated)}
            selectedItems={selectedItems}
            updateItems={(items: ITableFilterItem[]) => {
              if (items.length === 0) {
                infringementFilterDispatch({
                  type: InfringementTableDataActionType.CLEAR_FILTER,
                  payload: {
                    filter: ['includeOperationTags', 'excludeOperationTags'],
                  },
                });
                return;
              }

              const filterItem =
                items[0].key === 'NullValue' ? 'excludeOperationTags' : 'includeOperationTags';
              const keyValue = items[0].key === 'NullValue' ? NULL_VALUE : items[0].key;
              const list = keyValue ? ['Vector'] : [];

              infringementFilterDispatch({
                type: InfringementTableDataActionType.UPDATE_TAG_FILTER,
                payload: {
                  filter: filterItem,
                  resetFilter:
                    filterItem === 'includeOperationTags'
                      ? 'excludeOperationTags'
                      : 'includeOperationTags',
                  value: list,
                },
              });
            }}
            iconCategories={showSelectedIcons.map(
              (category: string) => infringementFilterCategories[category]
            )}
            languageData={{ clearValue, filterValue, noMatchesFound }}
          />
        );
      }
    } else {
      const originalItems = filterItems[category];
      if (originalItems !== undefined && originalItems.length > 0) {
        const transformedItems =
          nullable && nullable.includes(category) ? [NullValue, ...originalItems] : originalItems;
        if (
          category === 'infringementTypes' &&
          transformedItems.some(item => item.key === 'MinimumHeight')
        ) {
          const index = transformedItems.findIndex(item => item.key === 'MinimumHeight');
          if (index !== -1) {
            transformedItems[index] = { ...transformedItems[index], key: 'MinHeight' };
          }
        }

        filterComponents.push(
          <Filter
            key={category}
            categoryName={infringementFilterCategories[category]}
            filterItems={translateLabels(transformedItems, dataListUpdated)}
            selectedItems={(infringementFilterState[category] || []).map(item => ({
              key: item,
              label: item,
            }))}
            updateItems={(items: ITableFilterItem[]) => {
              infringementFilterDispatch({
                type: InfringementTableDataActionType.UPDATE_FILTER,
                payload: {
                  filter: category as keyof InfringementSummaryFilter,
                  value: items.map(item => item.key),
                },
              });
            }}
            iconCategories={showSelectedIcons.map(
              (category: string) => infringementFilterCategories[category]
            )}
            languageData={{ clearValue, filterValue, noMatchesFound }}
          />
        );
      }
    }
  });

  useEffect(() => {
    const startComplete = tempTimeRange?.start && isCompletedTimeFormat(tempTimeRange.start);
    const endComplete = tempTimeRange?.end && isCompletedTimeFormat(tempTimeRange.end);

    if (startComplete && endComplete) {
      setTimeLabel(`${tempTimeRange.start}-${tempTimeRange.end}`);
      infringementFilterDispatch({
        type: InfringementTableDataActionType.UPDATE_TIME_RANGE_FILTER,
        payload: {
          start: convertTimeStringToSeconds(tempTimeRange.start),
          end: convertTimeStringToSeconds(tempTimeRange.end),
        },
      });
    } else if (tempTimeRange?.start === '' && tempTimeRange?.end === '') {
      setTimeLabel('');
      infringementFilterDispatch({
        type: InfringementTableDataActionType.UPDATE_TIME_RANGE_FILTER,
        payload: { start: undefined, end: undefined },
      });
    }
  }, [tempTimeRange]);

  filterComponents.splice(
    1,
    0,
    <RangeSelector
      key="filter-time-selector"
      type="time"
      min={null}
      max={null}
      fromInputValue={tempTimeRange?.start}
      toInputValue={tempTimeRange?.end}
      setInputValue={(value: string, field: 'from' | 'to') => {
        handleSetTimeRange({
          start: field === 'from' ? value : tempTimeRange?.start,
          end: field === 'to' ? value : tempTimeRange?.end,
        });
      }}
      label={timeLabel}
      validateInputFormat={validateTimeFormat}
      isValidValue={isValidTime}
      onCloseValidation={onCloseTimeDialogValidation}
      isCompletedValueFormat={isCompletedTimeFormat}
      languageData={languageData}
    />
  );

  return (
    <div>
      <FilterRow
        filters={filterComponents}
        clearFilters={clearAllFilters}
        clearDisabled={!Object.keys(infringementFilterState).length}
        languageData={{ clearFilters }}
      />
    </div>
  );
};
